/**
 * Soft≠Live Page217 base + Page226 dual residual (survey_plane ∥ act_plane).
 *
 * Edge truth (W8 board surface · edge OS):
 *  - Unit stays on surveyed map_lat/lng — Soft Skill Weave may free-path; hardware must not.
 *  - Server never invents GPIO; pinmap_reported / seat states are Live only.
 *  - Dual buses: survey (read) ∥ act (commands · enqueue · alert)
 *
 * Buses:
 *   GET /ops/hw/status always-on dual posture
 *   survey: detail · presence · pins · ports · tele · caps
 *   act: POST commands · alert · enqueue · µDock hand-off
 * Soft residual deleted:
 *  - Soft free-roam pin invent for surveyed hardware
 *  - Soft GPIO / Soft command toast invent when plane sealed
 *
 * Mount: Ops side panel when a Live pin is selected from the map.
 * Rollback: pins v325/207 · drop dual status optional.
 */
import { useMemo, useState } from "react";
import { html, F, Ico, Sec, KV, Alert, go, stop } from "./ui.js";
import { path, useApi, useAction } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { presenceDiagnoseView } from "./fleet-live.js";
import { rssiOf, vbatOf } from "./device-overview-live.js";
import { deviceCapsViewOf } from "./device-caps-live.js";
import { confirmDeviceCommand } from "./device-command-gate.js";
import { poeLiveFromTelemetry } from "./board-telemetry-adapter.js";
import { DeviceCamerasLive, enqueueDeviceSnapshot } from "./device-cameras.js";
import { kindOf } from "./estate.js";
import { OpsHwMark, opsHwFacetsOf, opsHwStripOf } from "./ops-hw-mark.js";

const SECTIONS = [
  { id: "pulse", label: "Pulse" },
  { id: "board", label: "Board" },
  { id: "identity", label: "Identity" },
  { id: "link", label: "Link" },
  { id: "caps", label: "Caps" },
  { id: "cameras", label: "Cameras" },
  { id: "act", label: "Act" },
];

/** Soft≠Live: contract ops missing from client still resolve to edge paths. */
function safeDevPath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__ops_hw_status") return "/ops/hw/status";
    const id = opts && opts.pathParams && opts.pathParams[0];
    if (!id) return "";
    const enc = encodeURIComponent(id);
    if (op === "get__devices_p") return "/devices/" + enc;
    if (op === "get__devices_p_presence-diagnose") return "/devices/" + enc + "/presence-diagnose";
    if (op === "get__devices_p_pins") return "/devices/" + enc + "/pins";
    if (op === "get__devices_p_ports") return "/devices/" + enc + "/ports";
    if (op === "get__devices_p_board-telemetry") return "/devices/" + enc + "/board-telemetry";
    if (op === "get__devices_p_capabilities") return "/devices/" + enc + "/capabilities";
    if (op === "get__devices_p_storage_card" || op === "get__devices_p_storage_p") {
      return "/devices/" + enc + "/storage/card";
    }
    if (op === "post__devices_p_commands") return "/devices/" + enc + "/commands";
    // Must match OpenAPI: slash paths on device_control router (not underscore aliases).
    if (op === "post__devices_p_alert_on") {
      return "/devices/" + enc + "/alert/on?duration_ms=8000";
    }
    if (op === "post__devices_p_alert_off") return "/devices/" + enc + "/alert/off";
    return "";
  }
}

/** Soft operator error — never paint wire paths or raw status codes. */
export function softDeviceActErrorOf(err) {
  const n = String(err == null ? "" : err).trim().toLowerCase();
  if (!n) return "That action could not be completed.";
  if (/\b403\b|forbidden|can_send_command|can_alert|can_operate|insufficient|not in your scope|not found|out of scope/.test(n)) {
    return "Your account cannot control this unit. Ask a manager if you need access.";
  }
  if (/\b401\b|session|unauthorized|missing bearer/.test(n)) {
    return "Sign in again to continue.";
  }
  if (/\b404\b|not found|unknown device/.test(n)) {
    return "That unit is not available.";
  }
  if (/\b429\b|too many/.test(n)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable|disabled|revoked|offline/.test(n)) {
    return "This unit is offline to commands right now.";
  }
  if (/bind|snapshot|camera|enqueue|feature/.test(n)) {
    return "Snapshot could not start. Bind a camera first or ask a manager.";
  }
  if (/network|failed to fetch|timeout/.test(n)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/devices\/|\/ops\/|http|GET |POST |status_path|admin\/cameras/.test(n)) {
    return "That action could not be completed.";
  }
  return "That action could not be completed.";
}

/** Read-scope copy stays distinct from command authority. */
export function softDeviceReadErrorOf(err) {
  const n = String(err == null ? "" : err).trim().toLowerCase();
  if (/\b403\b|forbidden|not in your scope|out of scope/.test(n)) {
    return "This unit is outside your current device scope.";
  }
  if (/\b404\b|not found|unknown device/.test(n)) {
    return "This unit is no longer available in the Live register.";
  }
  if (/\b401\b|session|unauthorized|missing bearer/.test(n)) {
    return "Sign in again to read this unit.";
  }
  if (/network|failed to fetch|timeout/.test(n)) {
    return "Unit detail could not be refreshed — try again.";
  }
  return "Unit detail is unavailable; list state is not being treated as detail.";
}

/** Soft presence codes — no engineer bucket / wire tokens in the map desk. */
export function softPresenceLabelOf(view) {
  if (!view || typeof view !== "object") return null;
  const s = String(view.suspect || view.reason || "").toLowerCase();
  const b = String(view.bucket || "").toUpperCase();
  let health = "Status unknown";
  if (view.online === true) health = "Answering";
  else if (view.online === false) health = "Not answering";
  if (/stale|gap|heartbeat|timeout|silence/.test(s)) health = "Quiet longer than usual";
  if (/offline|lost|dead/.test(s)) health = "Offline";
  if (/mqtt|wifi|reconnect/.test(s)) health = "Link is flaky";
  if (b === "B0" || b === "B1") health = view.online ? "Answering" : "Not answering";
  const next = String(view.nextAction || "").trim();
  let tip = "No next step from the unit yet";
  if (/reboot|power/.test(next.toLowerCase())) tip = "Check power or restart on site if it stays quiet";
  else if (/wait|retry|recheck/.test(next.toLowerCase())) tip = "Wait a moment, then refresh";
  else if (/network|wifi|mqtt/.test(next.toLowerCase())) tip = "Check site network and access point";
  else if (next && !/\/|http|GET |POST /.test(next)) tip = next.slice(0, 120);
  return { health, tip };
}

/** Pure: dual seat from Live HW inspect posture (Page226). Fail-closed. */
export function opsHwInspectSeatOf(post, opts = {}) {
  if (opts.error) {
    return {
      surveyAllowed: false,
      boardReadAllowed: false,
      capsReadAllowed: false,
      actAllowed: false,
      reReadAllowed: true,
      commandWrite: false,
      alertWrite: false,
      enqueueWrite: false,
      role: String(opts.role || ""),
      detail: "ops_hw_inspect_status_error",
      detailLabel: "Could not confirm hardware access · try Refresh",
      enabled: false,
      boardSeatPaintMax: 128,
      capPaintMax: 24,
    };
  }
  if (opts.loading) {
    return {
      surveyAllowed: false,
      boardReadAllowed: false,
      capsReadAllowed: false,
      actAllowed: false,
      reReadAllowed: false,
      commandWrite: false,
      alertWrite: false,
      enqueueWrite: false,
      role: String(opts.role || ""),
      detail: "ops_hw_inspect_status_loading",
      detailLabel: "Confirming hardware seat…",
      enabled: false,
      boardSeatPaintMax: 128,
      capPaintMax: 24,
    };
  }
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  /* Fail-closed: planes only open on explicit Live true (no null invent). */
  const survey = p.survey_plane_allowed === true || p.detail_read_allowed === true;
  const act = p.act_plane_allowed === true || p.command_write_allowed === true;
  const seatCap = Number(lim.board_seat_paint_max);
  const capMax = Number(lim.cap_paint_max);
  const detail = p.detail || null;
  return {
    surveyAllowed: survey,
    boardReadAllowed: p.board_read_allowed === true,
    capsReadAllowed: p.caps_read_allowed === true,
    actAllowed: act,
    reReadAllowed: (p.re_read_allowed === true),
    commandWrite: (p.command_write_allowed === true),
    alertWrite: (p.alert_write_allowed === true),
    enqueueWrite: (p.enqueue_write_allowed === true),
    role: String(p.principal_role || opts.role || ""),
    detail,
    detailLabel: p.posture_label || null,
    enabled: p.enabled === true,
    boardSeatPaintMax:
      Number.isFinite(seatCap) && seatCap > 0 ? Math.floor(seatCap) : 128,
    capPaintMax:
      Number.isFinite(capMax) && capMax > 0 ? Math.floor(capMax) : 24,
  };
}

function strDash(v) {
  const s = String(v == null ? "" : v).trim();
  return s || "—";
}

function fmtAgeS(n) {
  if (n == null || !Number.isFinite(Number(n)) || Number(n) < 0) return "—";
  const s = Math.floor(Number(n));
  if (s < 60) return s + " s ago";
  if (s < 3600) return Math.floor(s / 60) + " m ago";
  return Math.floor(s / 3600) + " h ago";
}

function llFmt(ll) {
  if (!Array.isArray(ll) || ll.length < 2) return "—";
  const lng = Number(ll[0]);
  const lat = Number(ll[1]);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return "—";
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return "—";
  return lat.toFixed(5) + "°N  " + lng.toFixed(5) + "°E";
}

/**
 * Pure: compress GET /devices/{id}/pins + board-telemetry into operator KPIs.
 * Never invents GPIO numbers; empty seats stay empty.
 */
export function boardSurfaceSummaryOf(pinsEnv, teleEnv, portsEnv) {
  const root = pinsEnv && typeof pinsEnv === "object" ? pinsEnv : {};
  const items = Array.isArray(root.items)
    ? root.items
    : (Array.isArray(root.pins) ? root.pins : (Array.isArray(root.seats) ? root.seats : []));
  let assignable = 0;
  let assigned = 0;
  let verified = 0;
  let pending = 0;
  let sense = 0;
  let execute = 0;
  let unknownGpio = 0;
  for (const it of items) {
    if (!it || typeof it !== "object") continue;
    if (it.assignable === true) assignable += 1;
    const st = String(it.state || it.binding_state || "").toLowerCase();
    const role = String(it.role || "").trim();
    if (role) assigned += 1;
    if (st === "verified" || it.verified === true) verified += 1;
    if (st.includes("pending") || st === "claimed") pending += 1;
    const cls = String(it.seat_class || it.class || "").toLowerCase();
    if (cls === "sense") sense += 1;
    if (cls === "execute") execute += 1;
    if (it.assignable === true && (it.gpio == null || it.gpio === "")) unknownGpio += 1;
  }
  const tele = teleEnv && typeof teleEnv === "object" ? teleEnv : null;
  const conn = tele && tele.payload && tele.payload.telemetry
    ? tele.payload.telemetry.connections
    : (tele && tele.connections) || null;
  const epoch = conn && conn.epoch != null ? Number(conn.epoch) : null;
  const portsRoot = portsEnv && typeof portsEnv === "object" ? portsEnv : {};
  const portItems = Array.isArray(portsRoot.items)
    ? portsRoot.items
    : (Array.isArray(portsRoot.ports) ? portsRoot.ports : []);
  return {
    profileId: strDash(root.profile_id || root.board_profile),
    count: Number.isFinite(Number(root.count)) ? Number(root.count) : items.length,
    pinmapReported: root.pinmap_reported === true,
    assignable,
    assigned,
    verified,
    pending,
    sense,
    execute,
    unknownGpio,
    teleFresh: !!(tele && tele.fresh),
    teleAgeS: tele && tele.age_s != null ? Number(tele.age_s) : null,
    connectionEpoch: Number.isFinite(epoch) ? epoch : null,
    ports: portItems.length,
  };
}

/**
 * @param {{
 *   d: object,
 *   offline?: boolean,
 *   alarm?: object|null,
 *   onBack: function,
 *   onLive?: function,
 *   toast: function,
 * }} props
 */
export function OpsDeviceInspectDesk({ d, offline, alarm, onBack, onLive, toast }) {
  const did = String((d && d.id) || "").trim();
  const [sec, setSec] = useState("pulse");
  const act = useAction();
  const envelope = act.envelope;
  const posture = useApi(safeDevPath("get__ops_hw_status"));
  const post = posture.data || {};
  const seat = opsHwInspectSeatOf(post, {
    loading: !!posture.loading && !posture.data,
    error: !!posture.error && !posture.data,
  });

  const detailLive = useApi(
    did && seat.surveyAllowed
      ? safeDevPath("get__devices_p", { pathParams: [did] })
      : "",
  );
  const diagLive = useApi(
    did && seat.surveyAllowed
      ? safeDevPath("get__devices_p_presence-diagnose", { pathParams: [did] })
      : "",
  );
  const pinsLive = useApi(
    did && seat.boardReadAllowed
      ? safeDevPath("get__devices_p_pins", { pathParams: [did] })
      : "",
  );
  const portsLive = useApi(
    did && seat.boardReadAllowed
      ? safeDevPath("get__devices_p_ports", { pathParams: [did] })
      : "",
  );
  const teleLive = useApi(
    did && seat.boardReadAllowed
      ? safeDevPath("get__devices_p_board-telemetry", { pathParams: [did] })
      : "",
  );
  const capsLive = useApi(
    did && seat.capsReadAllowed
      ? safeDevPath("get__devices_p_capabilities", { pathParams: [did] })
      : "",
  );

  const detail = detailLive.data && typeof detailLive.data === "object" ? detailLive.data : null;
  const view = useMemo(() => presenceDiagnoseView(diagLive.data), [diagLive.data]);
  const board = useMemo(
    () => boardSurfaceSummaryOf(pinsLive.data, teleLive.data, portsLive.data),
    [pinsLive.data, teleLive.data, portsLive.data],
  );
  const caps = useMemo(() => deviceCapsViewOf(capsLive.data), [capsLive.data]);
  const poe = useMemo(() => poeLiveFromTelemetry(teleLive.data || null), [teleLive.data]);

  const online = detail && detail.is_online != null ? !!detail.is_online : !offline;
  const rssi = rssiOf(detail, d && d.rssi);
  const vbat = vbatOf(detail, d && d.vbat);
  const fw = detail ? strDash(detail.fw) : strDash(d && d.fw);
  const boardProfile = detail ? strDash(detail.board_profile) : "—";
  const chip = detail ? strDash(detail.chip_target) : "—";
  const sku = detail ? strDash(detail.hw_code_resolved || detail.product_hw_code) : "—";
  const position = detail && detail.map_lat != null && detail.map_lng != null
    ? [Number(detail.map_lng), Number(detail.map_lat)]
    : (d && d.ll);
  const ageS = view && view.ages
    ? (view.ages.signal != null ? view.ages.signal
      : (view.ages.heartbeat != null ? view.ages.heartbeat : view.ages.updated))
    : (Number.isFinite(Number(d && d.age)) && Number(d.age) >= 0 ? Number(d.age) : null);
  const k = kindOf((d && d.kind) || "node");
  const markDev = useMemo(() => ({
    ...(d || {}),
    label: (d && d.label) || did,
    fw,
    board_profile: boardProfile !== "—" ? boardProfile : "",
    chip_target: chip !== "—" ? chip : "",
    hw_code: sku !== "—" ? sku : "",
    claimedCaps: (caps && caps.claimed) || [],
    verifiedCaps: (caps && caps.verified) || [],
    device_type: (caps && caps.deviceType) || "",
    kind: (d && d.kind) || "",
  }), [d, did, fw, boardProfile, chip, sku, caps]);
  const facets = useMemo(() => opsHwFacetsOf(markDev), [markDev]);
  const strip = opsHwStripOf(facets.facets);

  const reReadAll = () => {
    if (!seat.reReadAllowed && !posture.loading) {
      toast && toast("Refresh is not available right now.", "warn");
      return;
    }
    posture.reload && posture.reload();
    detailLive.reload && detailLive.reload();
    diagLive.reload && diagLive.reload();
    pinsLive.reload && pinsLive.reload();
    portsLive.reload && portsLive.reload();
    teleLive.reload && teleLive.reload();
    capsLive.reload && capsLive.reload();
    toast && toast("Hardware data refreshed", "go");
  };

  const sendCmd = async (cmd, params, okMsg) => {
    if (!did) { toast("No unit selected", "warn"); return; }
    if (!seat.commandWrite) {
      toast && toast("Actions are closed for this seat.", "warn");
      return;
    }
    const ok = await confirmDeviceCommand({
      kind: cmd,
      targets: [{ id: did, label: (d && d.label) || did }],
      detail: "This reaches the selected board. Cancel leaves hardware untouched.",
    });
    if (!ok) return;
    const out = await act.run(
      safeDevPath("post__devices_p_commands", { pathParams: [did] }),
      { method: "POST", body: { cmd, params: params || {} } },
    );
    if (out) toast(okMsg || "Command sent", "go");
    else toast(softDeviceActErrorOf(act.error), "bad");
  };

  const soundSiren = async () => {
    if (!seat.alertWrite) {
      toast && toast("Actions are closed for this seat.", "warn");
      return;
    }
    const ok = await confirmDeviceCommand({
      kind: "alert_on",
      targets: [{ id: did, label: (d && d.label) || did }],
      detail: "Sounds the siren for a few seconds on this board.",
      confirmLabel: "Sound siren",
    });
    if (!ok) return;
    const out = await act.run(
      safeDevPath("post__devices_p_alert_on", { pathParams: [did] }),
      { method: "POST" },
    );
    if (out) toast("Siren on for a few seconds · " + ((d && d.label) || "unit"), "warn");
    else toast(softDeviceActErrorOf(act.error), "bad");
  };

  if (!posture.loading && post.enabled === false) {
    return html`<div className="ops-hw ops-hw--liquid" data-cmc-residual="page226-ops-hw-inspect">
      <button type="button" className="ops-hw__back btn btn--text"
          onClick=${(e) => { stop(e); onBack && onBack(); }}>
        <${Ico} d="arrowL" w=${13}/> Back to map</button>
      <${Alert} tone="warn" title="Hardware desk closed"
        say=${seat.detailLabel || "This account cannot open the hardware desk."}/>
      <button type="button" className="btn btn--ghost btn--sm"
        disabled=${!seat.reReadAllowed}
        onClick=${reReadAll}>Refresh</button>
    </div>`;
  }

  return html`<div className="ops-hw ops-hw--liquid" data-cmc-residual="page226-ops-hw-inspect"
      data-online=${online ? "1" : "0"} data-kind=${(d && d.kind) || "node"}>

    <button type="button" className="ops-hw__back btn btn--text"
        onClick=${(e) => { stop(e); onBack && onBack(); }}>
      <${Ico} d="arrowL" w=${13}/> Back to map</button>

    <header className="ops-hw__hero">
      <span className="ops-hw__crest ops-hw__crest--core" data-hw="1" data-cam=${facets.hasCam?"1":"0"}>
        <${OpsHwMark} device=${markDev} size=${44}/>
      </span>
      <div className="ops-hw__titles">
        <p className="ops-hw__k">Hardware</p>
        <h3 className="ops-hw__name">${(d && d.label) || did}</h3>
        <p className="ops-hw__id">${did}</p>
        <p className="ops-hw__roles">${strip}</p>
        <p className="ops-hw__honesty">
          Plant unit on the surveyed pin · camera is optional · does not free-roam.
        </p>
      </div>
      <em className="ops-hw__state"
          data-tone=${!online ? "bad" : (alarm ? "warn" : "go")}>
        ${!online ? "offline" : (alarm ? "alarmed" : "answering")}
      </em>
    </header>

    <div className="ops-hw__planes" role="group" aria-label="Hardware seats">
      <div className="ops-hw__plane" data-on=${seat.surveyAllowed ? "1" : "0"}>
        <span>Read</span>
        <b>${seat.surveyAllowed ? "open" : "closed"}</b>
        <em>details</em>
      </div>
      <div className="ops-hw__plane" data-on=${seat.actAllowed ? "1" : "0"}>
        <span>Act</span>
        <b>${seat.actAllowed ? "open" : "closed"}</b>
        <em>controls</em>
      </div>
    </div>
    <p className="note" style=${{ margin: "0 0 8px" }}>
      ${seat.detailLabel || (
        (seat.surveyAllowed ? "Reading open" : "Reading closed")
        + " · "
        + (seat.actAllowed ? "Actions open" : "Actions closed")
      )}
    </p>

    <div className="ops-hw__strip" aria-label="Site vitals" data-cmc-map-vitals=${rssi == null && vbat == null ? "empty" : (online ? "live" : "last")}
        data-cmc-map-rssi=${rssi == null ? "" : String(rssi)}
        data-cmc-map-vbat=${vbat == null ? "" : String(vbat)}>
      <div><span>Signal</span><b data-tone=${rssi == null ? "dim" : (!online || rssi < -72 ? "warn" : "hi")}>
        ${rssi == null ? "—" : (rssi + " dBm")}</b></div>
      <div><span>Battery</span><b data-tone=${vbat == null ? "dim" : (!online || vbat < 3.7 ? "warn" : "hi")}>
        ${vbat == null ? "—" : (Number(vbat).toFixed(2) + " V")}</b></div>
      <div><span>Heard</span><b data-tone=${ageS == null ? "dim" : (!online ? "warn" : "hi")}>
        ${ageS == null ? "—" : fmtAgeS(ageS)}</b></div>
      <div><span>Firmware</span><b data-tone=${fw === "—" ? "dim" : ""}>${fw}</b></div>
    </div>
    ${!online && (rssi != null || vbat != null)
      ? html`<p className="note" data-cmc-map-vitals-note="1">Last report. The unit is quiet, so this is not a live meter.</p>`
      : null}

    <div className="ops-hw__tools">
      <button type="button" className="btn btn--ghost btn--sm"
        disabled=${!seat.reReadAllowed}
        onClick=${reReadAll}>
        <${Ico} d="refresh" w=${13}/> Refresh</button>
      <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => go("/machine/" + encodeURIComponent(did))}>
        <${Ico} d="sliders" w=${13}/> Full board desk</button>
    </div>

    ${!seat.surveyAllowed ? html`
      <${Alert} tone="warn" title="Reading closed"
        say="Board details stay hidden until this seat can read the hardware."/>`
      : null}
    ${seat.surveyAllowed && !seat.actAllowed ? html`
      <${Alert} tone="info" title="Actions closed"
        say="You can read this unit. Commands and siren stay sealed for this seat."/>`
      : null}
    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}

    ${alarm ? html`
      <div className="ops-hw__alarm flat">
        <${KV} k=${alarm.id || "Alarm"} v=${(alarm.by || "open") + " · " + (alarm.ts || "")} tone="warn"/>
      </div>` : null}

    <nav className="ops-hw__secs" aria-label="Hardware facets">
      ${SECTIONS.map((s) => html`
        <button type="button" key=${s.id} data-on=${sec === s.id ? "1" : "0"}
            onClick=${() => setSec(s.id)}>${s.label}</button>`)}
    </nav>

    <div className="ops-hw__body">
      ${sec === "pulse" && html`<${F}>
        <${Sec} title="Presence">
          ${!seat.surveyAllowed
            ? html`<${Alert} tone="info" title="Reading closed" say="Presence stays empty until this seat can read the hardware."/>`
            : html`<${F}>
          ${detailLive.error && !detail
            ? html`<${Alert} tone="warn" title="Detail unavailable" say=${softDeviceReadErrorOf(detailLive.error)}/>`
            : null}
          <${KV} k=${detail ? "Online" : "List state"}
            v=${detail ? (online ? "online" : "offline") : (offline ? "offline" : "online · detail unconfirmed")}
            tone=${detail ? (online ? "hi" : "bad") : "dim"}/>
          <${KV} k="Zone" v=${strDash(d && d.zone)} tone=${d && d.zone ? "" : "dim"}/>
          <${KV} k="Group" v=${strDash(d && d.group)} tone=${d && d.group ? "" : "dim"}/>
          <${KV} k="Surveyed ll" v=${llFmt(position)} tone=${position ? "hi" : "dim"}/>
          <${KV} k="Edge roles" v=${strip} tone="hi"/>
          <${KV} k="Kind hint" v=${k.t || strDash(d && d.kind)} tone="dim"/>
          ${view ? (() => {
            const soft = softPresenceLabelOf(view);
            return html`<${F}>
            <${KV} k="Health" v=${(soft && soft.health) || "—"}
              tone=${view.online ? "hi" : "warn"}/>
            <${KV} k="Next step" v=${(soft && soft.tip) || "—"}
              tone=${soft && soft.tip ? "" : "dim"}/>
          <//>`;
          })() : null}
          <p className="note">Missing sensors stay blank · this pin stays on the surveyed plant seat.</p>
            <//>`}
        <//>
      <//>`}

      ${sec === "board" && html`<${F}>
        <${Sec} title="Edge board surface">
          ${!seat.surveyAllowed
            ? html`<${Alert} tone="info" title="Reading closed" say="Board pins stay empty until read is open."/>`
            : !seat.boardReadAllowed
              ? html`<${Alert} tone="info" title="Board reads off"
                  say="Pin, port, and telemetry reads stay off on this deployment. Identity and presence stay on the other tabs."/>`
            : html`<${F}>
          ${pinsLive.error && !pinsLive.data
            ? html`<${Alert} tone="warn" title="Board pins partial"
                say=${/503|disabled|not found|404|board_profile/i.test(String(pinsLive.error))
                  ? "Board map not ready yet — pins stay empty until the unit reports them (never invents wire numbers)."
                  : softDeviceActErrorOf(pinsLive.error)}/>`
            : null}
          <${KV} k="Profile" v=${board.profileId !== "—" ? board.profileId : boardProfile}
            tone=${board.profileId === "—" && boardProfile === "—" ? "dim" : "hi"}/>
          <${KV} k="Pinmap reported"
            v=${board.pinmapReported ? "yes · device checked in" : "no · waiting for first board report"}
            tone=${board.pinmapReported ? "hi" : "warn"}/>
          <${KV} k="Seats" v=${String(board.count)}/>
          <${KV} k="Assignable / assigned"
            v=${board.assignable + " / " + board.assigned}
            tone=${board.assigned ? "" : "dim"}/>
          <${KV} k="Verified bindings" v=${String(board.verified)}
            tone=${board.verified ? "hi" : "dim"}/>
          <${KV} k="Pending / claimed" v=${String(board.pending)}
            tone=${board.pending ? "warn" : "dim"}/>
          <${KV} k="Seats without GPIO yet"
            v=${String(board.unknownGpio)}
            tone=${board.unknownGpio ? "warn" : "dim"}/>
          <${KV} k="I/O ports" v=${String(board.ports)} tone=${board.ports ? "" : "dim"}/>
          <${KV} k="Board health"
            v=${board.teleFresh
              ? ("fresh" + (board.teleAgeS != null ? (" · " + board.teleAgeS + " s") : ""))
              : (board.teleAgeS != null ? ("stale · " + board.teleAgeS + " s") : "no frame yet")}
            tone=${board.teleFresh ? "hi" : "warn"}/>
          <${KV} k="Link watch #"
            v=${board.connectionEpoch == null ? "—" : String(board.connectionEpoch)}
            tone=${board.connectionEpoch == null ? "dim" : "hi"}/>
          <p className="note">Only verified seat bindings can drive hardware. Empty seats stay empty — this desk never invents wire numbers.</p>
          <div className="btn-row" style=${{ marginTop:10 }}>
            <button type="button" className="btn btn--go btn--sm"
                onClick=${() => go("/machine/" + encodeURIComponent(did))}>
              Open board desk</button>
          </div>
            <//>`}
        <//>
      <//>`}

      ${sec === "identity" && html`<${F}>
        <${Sec} title="Hardware identity">
          ${!seat.surveyAllowed
            ? html`<p className="note">Reading closed · identity stays empty.</p>`
            : html`<${F}>
          <${KV} k="SKU / hw" v=${sku} tone=${sku === "—" ? "dim" : "hi"}/>
          <${KV} k="Board profile" v=${boardProfile} tone=${boardProfile === "—" ? "dim" : ""}/>
          <${KV} k="Chip target" v=${chip} tone=${chip === "—" ? "dim" : ""}/>
          <${KV} k="MAC" v=${detail ? strDash(detail.mac_display || detail.mac_nocolon || detail.mac) : "—"}
            tone="dim"/>
          <${KV} k="Firmware" v=${fw}/>
          <p className="note">Rename labels on the Fleet device desk. Map inspect stays read-first for ops tempo.</p>
            <//>`}
        <//>
      <//>`}

      ${sec === "link" && html`<${F}>
        <${Sec} title="Link & power">
          ${!seat.surveyAllowed
            ? html`<p className="note">Reading closed · link meters stay empty.</p>`
            : html`<${F}>
          <${KV} k="Signal"
            v=${rssi == null ? "—" : (rssi + " dBm")}
            tone=${rssi == null ? "dim" : (rssi < -72 ? "warn" : "hi")}/>
          <${KV} k="Battery"
            v=${vbat == null ? "—" : (Number(vbat).toFixed(2) + " V")}
            tone=${vbat == null ? "dim" : (vbat < 3.8 ? "warn" : "hi")}/>
          <${KV} k="PoE"
            v=${!seat.boardReadAllowed
              ? "off"
              : (poe && poe.fresh && poe.fields
              ? "Fresh board reading"
              : (poe && poe.fields ? "present · not fresh" : "—"))}
            tone=${seat.boardReadAllowed && poe && poe.fresh ? "hi" : "dim"}/>
          ${!seat.boardReadAllowed
            ? html`<p className="note">Board power meters stay off on this deployment.</p>`
            : poe && poe.fields ? html`<${F}>
            ${Object.entries(poe.fields).slice(0, 6).map(([k0, v0]) => html`
              <${KV} key=${k0} k=${k0} v=${v0 == null ? "—" : String(v0)}/>`)}
          <//>` : html`<p className="note">PoE meters appear when board-telemetry silicon carries a fresh PD frame.</p>`}
            <//>`}
        <//>
      <//>`}

      ${sec === "caps" && html`<${F}>
        <${Sec} title="Capabilities">
          ${!seat.surveyAllowed
            ? html`<p className="note">Reading closed · capabilities stay empty.</p>`
            : !seat.capsReadAllowed
              ? html`<p className="note">Capability registry stays off on this deployment.</p>`
            : html`<${F}>
          ${capsLive.error && !capsLive.data
            ? html`<${Alert} tone="warn" title="Registry partial"
                say=${/503|disabled/i.test(String(capsLive.error))
                  ? "Capability registry is off for this unit."
                  : softDeviceActErrorOf(capsLive.error)}/>`
            : null}
          <${KV} k="Verified" v=${String((caps.verified || []).length)} tone="hi"/>
          <${KV} k="Claimed only" v=${String((caps.claimed || []).length)}/>
          <div className="ops-hw__caplist">
            ${(caps.verified || []).slice(0, seat.capPaintMax).map((id) => html`
              <span key=${"v-" + id} data-st="verified">${id}</span>`)}
            ${(caps.claimed || []).filter((id) => !(caps.verifiedSet && caps.verifiedSet.has(id)))
              .slice(0, seat.capPaintMax).map((id) => html`
              <span key=${"c-" + id} data-st="claimed">${id}</span>`)}
            ${!(caps.verified || []).length && !(caps.claimed || []).length
              ? html`<p className="note" style=${{ margin:0 }}>No claimed capabilities on the account registry yet.</p>`
              : null}
          </div>
            <//>`}
        <//>
      <//>`}

      ${sec === "cameras" && html`<${F}>
        <${Sec} title="Cameras" meta="Bind · health · snapshot">
          <div className="btn-row" style=${{ marginBottom:10 }}>
            <button type="button" className="btn btn--go btn--sm"
                disabled=${act.busy || !seat.enqueueWrite}
                onClick=${async () => {
                  if (!seat.enqueueWrite) {
                    toast && toast("Actions are closed for this seat.", "warn");
                    return;
                  }
                  const softToast = (msg, tone) => {
                    if (!toast) return;
                    const t = String(tone || "go");
                    if (t === "bad" || t === "warn") {
                      toast(softDeviceActErrorOf(msg), t);
                      return;
                    }
                    // Soft success: no wire path chips · no engineer enqueue codes.
                    const m = String(msg || "");
                    if (/\/|POST |GET |Live ·|trigger /.test(m)) {
                      toast("Snapshot queued for this unit.", "go");
                      return;
                    }
                    toast(m || "Snapshot queued for this unit.", "go");
                  };
                  await enqueueDeviceSnapshot(act, did, {
                    alarmId: alarm && alarm.id,
                    toast: softToast,
                    reason: "Snapshot from map hardware desk",
                  });
                }}>
              <${Ico} d="camera" w=${13}/> Queue snapshot</button>
            <button type="button" className="btn btn--ghost btn--sm"
                onClick=${() => go("/ops/cameras")}>Site cameras</button>
          </div>
          ${seat.surveyAllowed || seat.actAllowed
            ? html`<${DeviceCamerasLive} deviceId=${did} toast=${toast}/>`
            : html`<p className="note">Hardware seat closed · camera list stays empty.</p>`}
        <//>
      <//>`}

      ${sec === "act" && html`<${F}>
        <${Sec} title="Actions" meta="Ping · siren · watch">
          ${!seat.actAllowed
            ? html`<${Alert} tone="info" title="Actions closed"
                say="Commands and siren stay sealed. Reading can stay independent."/>`
            : null}
          ${act.error ? html`<${Alert} tone="warn" title="Last attempt"
            say=${softDeviceActErrorOf(act.error)}/>` : null}
          <div className="btn-row">
            <button type="button" className="btn btn--ghost btn--sm"
                disabled=${!online || act.busy || !seat.commandWrite}
                onClick=${() => sendCmd("ping", {}, "Ping sent · " + ((d && d.label) || "unit"))}>
              <${Ico} d="refresh" w=${13}/> Ping</button>
            ${(d && d.kind) === "siren" && html`
              <button type="button" className="btn btn--danger btn--sm"
                  disabled=${!online || act.busy || !seat.alertWrite} onClick=${soundSiren}>
                <${Ico} d="siren" w=${13}/> Sound siren</button>`}
            ${online && onLive ? html`
              <button type="button" className="btn btn--go btn--sm"
                  disabled=${!seat.actAllowed}
                  onClick=${() => seat.actAllowed && onLive(d)}>
                <${Ico} d="play" w=${13}/> Watch live</button>` : null}
          </div>
          ${!online ? html`<p className="note">Commands stay off while the unit is unreachable. Snapshots may still queue when cameras are bound.</p>` : null}
        <//>
      <//>`}
    </div>
  </div>`;
}
